/**
 * Register Page
 * 
 * @validates Requirements 1.1, 1.3, 1.4, 1.5 - Registration with validation
 */

import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import { Button, Input, Card } from '../../components/ui';
import { getCurrentLanguage } from '../../i18n';
import { authService } from '../../services/auth.service';
import { AxiosError } from 'axios';
import { ApiResponse } from '@budgira/shared';
import GoogleLoginButton from '../../components/auth/GoogleLoginButton';

const RegisterPage = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { clearError } = useAuthStore();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localError, setLocalError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [googleError, setGoogleError] = useState<string | null>(null);

  // Check for Google OAuth errors
  useEffect(() => {
    const googleErrorParam = searchParams.get('error');
    if (googleErrorParam) {
      setGoogleError(googleErrorParam);
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) return t('auth.register.errors.passwordLength');
    if (!/[A-Z]/.test(pwd)) return t('auth.register.errors.passwordUppercase');
    if (!/[a-z]/.test(pwd)) return t('auth.register.errors.passwordLowercase');
    if (!/[0-9]/.test(pwd)) return t('auth.register.errors.passwordDigit');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) return t('auth.register.errors.passwordSpecial');
    return null;
  };

  const getErrorMessage = (errorCode: string | undefined): string => {
    switch (errorCode) {
      case 'FORBIDDEN':
        return t('auth.register.errors.registrationDisabled');
      case 'EMAIL_EXISTS':
      case 'EMAIL_ALREADY_EXISTS':
      case 'CONFLICT':
        return t('auth.register.errors.emailExists');
      default:
        return t('common.error');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setLocalError('');

    // Validate password
    const passwordError = validatePassword(password);
    if (passwordError) {
      setLocalError(passwordError);
      return;
    }

    // Check passwords match
    if (password !== confirmPassword) {
      setLocalError(t('auth.register.errors.passwordMismatch'));
      return;
    }

    setIsLoading(true);
    try {
      const response = await authService.register({ 
        email, 
        password, 
        language: getCurrentLanguage() 
      });
      
      if (response.success) {
        setSuccess(true);
      } else {
        // Handle API error response
        const errorCode = response.error?.code;
        setLocalError(getErrorMessage(errorCode));
      }
    } catch (err) {
      // Handle axios error (e.g., 403, 409)
      const axiosError = err as AxiosError<ApiResponse<unknown>>;
      const errorCode = axiosError.response?.data?.error?.code;
      setLocalError(getErrorMessage(errorCode));
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <Card>
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-green-100 dark:bg-green-900/40 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-2">{t('auth.register.success.title')}</h2>
          <p className="text-slate-500 dark:text-slate-400 mb-6">{t('auth.register.success.message')}</p>
          <Link to="/auth/login">
            <Button className="w-full">{t('auth.register.success.login')}</Button>
          </Link>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{t('auth.register.title')}</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">{t('auth.register.subtitle')}</p>
      </div>

      {googleError && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg text-sm text-red-600 dark:text-red-400">
          {t(`auth.google.errors.${googleError}`, t('auth.google.errors.generic', 'Erreur lors de l\'inscription avec Google'))}
        </div>
      )}

      {/* Google Sign Up Button */}
      <GoogleLoginButton className="mb-4" isSignUp />

      {/* Divider */}
      <div className="relative my-4">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-slate-200 dark:border-slate-700"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400">
            {t('auth.register.orWithEmail', 'ou avec votre email')}
          </span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label={t('auth.email')}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="email@example.com"
          required
          autoComplete="email"
        />

        <Input
          label={t('auth.password')}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          required
          autoComplete="new-password"
          helperText={t('auth.register.passwordHint')}
        />

        <Input
          label={t('auth.confirmPassword')}
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="••••••••"
          required
          autoComplete="new-password"
        />

        {localError && (
          <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg text-sm text-red-600 dark:text-red-400">
            {localError}
          </div>
        )}

        <Button type="submit" className="w-full" isLoading={isLoading}>
          {t('auth.register.submit')}
        </Button>
      </form>

      <div className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">
        {t('auth.register.hasAccount')}{' '}
        <Link to="/auth/login" className="text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 font-medium">
          {t('auth.register.login')}
        </Link>
      </div>
    </Card>
  );
};

export default RegisterPage;
